import { isRegExp } from 'lodash-es';
import { GraphType } from '@/graphql/types';
import { ValueScaleEquation } from '../../../../../graph/auto-scale';
import { availableAxis, type AxisType } from '../../../../../graph/axis';
import { getDataType } from '../../../../../graph/data-types';
import { type DataTypeValue, ScaleType } from '../../../../../graph/enums';
import { LegendData } from '../../../../../graph/legend-helpers';
import { applyNamingConvention } from '../../../../../utils/naming-conventions';
import { isRecord } from '../../../../../utils/typeGuards';
import type {
    ElementSettings,
    ElementSettingsFormField,
    ElementSettingsFormSchema,
} from '../types';
import {
    createDefaultGraphSettings,
    dataTypeSupportsGraphType,
    getAvailableGraphTypes,
    getGraphTypeFormSettingNames,
    getGraphTypeHiddenLineExcludeTypes,
    getGraphTypeOwnedDataTypeSettings,
    getGraphTypePersistedSettingNames,
    GRAPH_SETTINGS_ALLOWED_KEYS,
    isSupportedGraphType,
    LEGEND_PLACEMENT,
    LEGEND_PLACEMENTS,
    resolveReportBuilderLegendPlacement,
    type LegendPlacement,
    type SupportedGraphType,
} from './contracts';

const SCALE_SETTING_KEYS = ['valueScale', 'frequencyScale'] as const;
const HIDDEN_LINES_MODE_FIELD = 'hiddenLinesMode';
const HIDDEN_LINES_MODE = {
    INHERIT: 'inherit',
    CUSTOM: 'custom',
} as const;
type HiddenLinesMode = (typeof HIDDEN_LINES_MODE)[keyof typeof HIDDEN_LINES_MODE];
type EnabledAxesFormSettings = Partial<Record<AxisType, unknown>>;

function hasOwnSetting(settings: ElementSettings, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(settings, key);
}

function createChoiceOptions<TValue extends string>(
    values: readonly TValue[],
    getLabel: (value: TValue) => string
): Array<[TValue, string]> {
    return values.map((value) => [value, getLabel(value)]);
}

function createPreferredGraphTypeOptions(): Array<[GraphType, string]> {
    return [
        [GraphType.Bar, gettext('GRAPH_TYPE_OPTION_BAR')],
        [GraphType.Dot, gettext('GRAPH_TYPE_OPTION_DOT')],
        [GraphType.Line, gettext('GRAPH_TYPE_OPTION_LINE')],
        [GraphType.Step, gettext('GRAPH_TYPE_OPTION_STEP')],
    ];
}

function createFrequencyGraphScaleTypeOptions(): Array<[ScaleType, string]> {
    return [
        [ScaleType.Auto, gettext('SCALE_TYPE_AUTO')],
        [ScaleType.Linear, gettext('SCALE_TYPE_LINEAR')],
        [ScaleType.Logarithmic, gettext('SCALE_TYPE_LOGARITHMIC')],
    ];
}

function createValueScaleEquationOptions(): Array<[string, string]> {
    return createChoiceOptions(Object.values(ValueScaleEquation), (value) => value);
}

function createDataTypeOptions(
    dataTypes: readonly DataTypeValue[]
): Array<[DataTypeValue, string]> {
    return createChoiceOptions(dataTypes, (value) =>
        applyNamingConvention(getDataType(value).title)
    );
}

function getCurrentGraphType(currentSettings?: ElementSettings): SupportedGraphType {
    if (currentSettings?.graphType === 'frequency' || currentSettings?.graphType === 'veff_day') {
        return currentSettings.graphType;
    }

    return 'velocity';
}

function createGraphTypeOptions(
    availableGraphTypes: readonly SupportedGraphType[]
): Array<[SupportedGraphType, string]> {
    const labels: Record<SupportedGraphType, string> = {
        velocity: gettext('VELOCITY'),
        frequency: gettext('FREQUENCY'),
        veff_day: gettext('VPER_DAY_GRAPH'),
    };
    return createChoiceOptions(availableGraphTypes, (value) =>
        applyNamingConvention(labels[value])
    );
}

function createLegendPlacementOptions(): Array<[LegendPlacement, string]> {
    const labels: Record<LegendPlacement, string> = {
        [LEGEND_PLACEMENT.OVERLAY]: gettext('GRAPH_LEGEND_OVERLAY'),
        [LEGEND_PLACEMENT.HEADER]: gettext('GRAPH_LEGEND_HEADER'),
        [LEGEND_PLACEMENT.HIDDEN]: gettext('GRAPH_LEGEND_HIDDEN'),
    };
    return createChoiceOptions(LEGEND_PLACEMENTS, (value) => labels[value]);
}

function createEnabledAxesDefault(currentSettings?: ElementSettings): Record<AxisType, boolean> {
    const enabledAxesSetting = currentSettings?.enabledAxes;
    const enabledAxes = Array.isArray(enabledAxesSetting)
        ? new Set(
              enabledAxesSetting.filter((axis): axis is AxisType =>
                  availableAxis.includes(axis as AxisType)
              )
          )
        : null;

    function isAxisEnabled(axis: AxisType): boolean {
        if (isRecord(enabledAxesSetting)) {
            return enabledAxesSetting[axis] === true;
        }

        return enabledAxes ? enabledAxes.has(axis) : true;
    }

    return Object.fromEntries(availableAxis.map((axis) => [axis, isAxisEnabled(axis)])) as Record<
        AxisType,
        boolean
    >;
}

function isLegendTypeExcluded(
    excludeTypes: readonly (string | RegExp)[],
    entryType: string
): boolean {
    return excludeTypes.some((excludeType) =>
        isRegExp(excludeType) ? excludeType.test(entryType) : excludeType === entryType
    );
}

/**
 * A legend entry is offered as a hideable line when at least one of the data types available for
 * the current context can render it. `legendExcludeTypes` is the same per-data-type declaration the
 * rendered graph legend uses, so the checkbox list cannot drift from what the graph actually draws.
 */
function isLegendEntryRenderableForDataTypes(
    entryType: string,
    dataTypes: readonly DataTypeValue[]
): boolean {
    return dataTypes.some(
        (dataType) =>
            !isLegendTypeExcluded(getDataType(dataType).legendExcludeTypes ?? [], entryType)
    );
}

function getHiddenLineEntries(
    dataTypes: readonly DataTypeValue[],
    includeEnabledAxesField: boolean,
    graphType: SupportedGraphType
) {
    const graphTypeExcludeTypes = getGraphTypeHiddenLineExcludeTypes(graphType);

    return (
        new LegendData()
            .getEntries()
            .filter(
                (entry) =>
                    !includeEnabledAxesField || !availableAxis.includes(entry.type as AxisType)
            )
            .filter((entry) => !graphTypeExcludeTypes.includes(entry.type))
            // Non-clickable entries are informational headers rather than toggleable lines.
            .filter((entry) => entry.clickable)
            .filter((entry) => isLegendEntryRenderableForDataTypes(entry.type, dataTypes))
    );
}

function createHiddenLinesField(
    currentSettings: ElementSettings | undefined,
    dataTypes: readonly DataTypeValue[],
    includeEnabledAxesField: boolean,
    graphType: SupportedGraphType
): ElementSettingsFormField {
    const hiddenLinesSetting = currentSettings?.hiddenLines;
    const hiddenLines = new Set(Array.isArray(hiddenLinesSetting) ? hiddenLinesSetting : []);
    const entries = getHiddenLineEntries(dataTypes, includeEnabledAxesField, graphType);
    const defaultHiddenLines = Object.fromEntries(
        entries.map((entry) => [entry.type, hiddenLines.has(entry.type)])
    );

    return {
        name: 'hiddenLines',
        label: '',
        type: 'group',
        columns: 4,
        required: false,
        default: defaultHiddenLines,
        fields: entries.map((entry) => ({
            name: entry.type,
            label: entry.title || entry.type,
            type: 'boolean',
            required: false,
            default: hiddenLines.has(entry.type),
        })),
    };
}

function getHiddenLinesMode(currentSettings?: ElementSettings): HiddenLinesMode {
    const mode = currentSettings?.[HIDDEN_LINES_MODE_FIELD];
    if (mode === HIDDEN_LINES_MODE.INHERIT || mode === HIDDEN_LINES_MODE.CUSTOM) {
        return mode;
    }

    return hasOwnSetting(currentSettings ?? {}, 'hiddenLines')
        ? HIDDEN_LINES_MODE.CUSTOM
        : HIDDEN_LINES_MODE.INHERIT;
}

function createHiddenLinesModeField(mode: HiddenLinesMode): ElementSettingsFormField {
    return {
        name: HIDDEN_LINES_MODE_FIELD,
        label: gettext('HIDDEN_LINES'),
        type: 'choice',
        required: true,
        options: [
            [HIDDEN_LINES_MODE.INHERIT, gettext('INHERIT')],
            [HIDDEN_LINES_MODE.CUSTOM, gettext('CUSTOM')],
        ],
        validateOptions: true,
        default: mode,
    };
}

function createEnabledAxesField(currentSettings?: ElementSettings): ElementSettingsFormField {
    const defaultEnabledAxes = createEnabledAxesDefault(currentSettings);

    return {
        name: 'enabledAxes',
        label: gettext('AXIS'),
        type: 'group',
        columns: 4,
        required: false,
        default: defaultEnabledAxes,
        fields: availableAxis.map((axis) => ({
            name: axis,
            label: axis,
            type: 'boolean',
            required: false,
            default: defaultEnabledAxes[axis],
        })),
    };
}

function normalizeEnabledAxesForSave(nextSettings: ElementSettings): ElementSettings {
    const enabledAxesValue = nextSettings.enabledAxes;
    if (!isRecord(enabledAxesValue)) {
        return nextSettings;
    }

    const enabledAxesFormSettings: EnabledAxesFormSettings = enabledAxesValue;
    const hasAxisFormSetting = availableAxis.some((axis) =>
        hasOwnSetting(enabledAxesFormSettings, axis)
    );
    const normalizedSettings = { ...nextSettings };

    if (!hasAxisFormSetting) {
        delete normalizedSettings.enabledAxes;
        return normalizedSettings;
    }

    normalizedSettings.enabledAxes = availableAxis.filter(
        (axis) => enabledAxesFormSettings[axis] === true
    );
    return normalizedSettings;
}

function preserveExplicitNullScaleOverrides(
    currentSettings: ElementSettings,
    nextSettings: ElementSettings
): ElementSettings {
    let normalizedSettings = nextSettings;

    SCALE_SETTING_KEYS.forEach((key) => {
        if (currentSettings[key] !== null || hasOwnSetting(nextSettings, key)) {
            return;
        }

        if (normalizedSettings === nextSettings) {
            normalizedSettings = { ...nextSettings };
        }

        normalizedSettings[key] = null;
    });

    return normalizedSettings;
}

function normalizeHiddenLinesForSave(nextSettings: ElementSettings): ElementSettings {
    const hiddenLinesMode = nextSettings[HIDDEN_LINES_MODE_FIELD];
    const hiddenLinesValue = nextSettings.hiddenLines;
    const normalizedSettings = { ...nextSettings };
    delete normalizedSettings[HIDDEN_LINES_MODE_FIELD];

    if (hiddenLinesMode === HIDDEN_LINES_MODE.INHERIT) {
        delete normalizedSettings.hiddenLines;
        return normalizedSettings;
    }

    if (hiddenLinesMode !== HIDDEN_LINES_MODE.CUSTOM || !isRecord(hiddenLinesValue)) {
        throw new Error('Expected hidden lines mode and custom hidden lines settings.');
    }

    normalizedSettings.hiddenLines = Object.entries(hiddenLinesValue)
        .filter(([, hidden]) => hidden === true)
        .map(([line]) => line);

    return normalizedSettings;
}

function preserveImplicitSetting(
    currentSettings: ElementSettings,
    nextSettings: ElementSettings,
    touchedSettingNames: ReadonlySet<string> | undefined,
    settingName: string
): ElementSettings {
    if (hasOwnSetting(currentSettings, settingName) || touchedSettingNames?.has(settingName)) {
        return nextSettings;
    }

    const normalizedSettings = { ...nextSettings };
    delete normalizedSettings[settingName];
    return normalizedSettings;
}

function applyGraphTypeOwnedDataTypeSettings(
    nextSettings: ElementSettings,
    graphType: SupportedGraphType
): ElementSettings {
    return {
        ...nextSettings,
        ...getGraphTypeOwnedDataTypeSettings(graphType),
    };
}

function removeSettingsNotUsedByGraphType(
    nextSettings: ElementSettings,
    graphType: SupportedGraphType
): ElementSettings {
    const supportedSettingNames = new Set<string>([
        'graphType',
        'width',
        'height',
        ...getGraphTypePersistedSettingNames(graphType),
    ]);
    const normalizedSettings = { ...nextSettings };

    GRAPH_SETTINGS_ALLOWED_KEYS.forEach((settingName) => {
        if (!supportedSettingNames.has(settingName)) {
            delete normalizedSettings[settingName];
        }
    });

    return normalizedSettings;
}

export function createGraphSettingsFormSchema(
    currentSettings: ElementSettings | undefined,
    dataTypes: readonly DataTypeValue[],
    includeEnabledAxesField: boolean
): ElementSettingsFormSchema {
    const defaults = createDefaultGraphSettings();
    const graphType = getCurrentGraphType(currentSettings);
    const compatibleDataTypes = dataTypes.filter((dataType) =>
        dataTypeSupportsGraphType(dataType, graphType)
    );
    const availableGraphTypes = getAvailableGraphTypes(dataTypes);
    const isFrequencyGraph = graphType === 'frequency';
    const hiddenLinesMode = getHiddenLinesMode(currentSettings);

    if (isFrequencyGraph && compatibleDataTypes.length === 0) {
        throw new Error('No data types available for frequency graph.');
    }

    const fieldsByName: Partial<Record<string, ElementSettingsFormField>> = {
        graphType: {
            name: 'graphType',
            label: gettext('GRAPH_TYPE'),
            type: 'choice',
            required: true,
            options: createGraphTypeOptions(availableGraphTypes),
            default: defaults.graphType,
        },
        width: {
            name: 'width',
            label: gettext('WIDTH'),
            type: 'float',
            required: true,
            minValue: 1,
            default: defaults.width,
        },
        height: {
            name: 'height',
            label: gettext('HEIGHT'),
            type: 'float',
            required: true,
            minValue: 1,
            default: defaults.height,
        },
        legendPlacement: {
            name: 'legendPlacement',
            label: gettext('GRAPH_LEGEND'),
            type: 'choice',
            required: true,
            options: createLegendPlacementOptions(),
            validateOptions: true,
            default: resolveReportBuilderLegendPlacement(currentSettings),
        },
        preferredGraphType: {
            name: 'preferredGraphType',
            label: `${gettext('GRAPH_TYPE')} (${gettext('GRAPH_TYPE_OPTION_LINE')}/${gettext(
                'GRAPH_TYPE_OPTION_BAR'
            )})`,
            type: 'choice',
            required: false,
            options: [['', gettext('SELECT_OPTION')], ...createPreferredGraphTypeOptions()],
            validateOptions: true,
            default: '',
        },
        frequencyGraphScaleType: {
            name: 'frequencyGraphScaleType',
            label: gettext('SCALE_TYPE_LABEL'),
            type: 'choice',
            required: false,
            options: [['', gettext('SELECT_OPTION')], ...createFrequencyGraphScaleTypeOptions()],
            validateOptions: true,
            default: '',
        },
        dataType: {
            name: 'dataType',
            label: gettext('DATA_TYPE'),
            type: 'choice',
            // Frequency graphs render blank for data types that don't support
            // them (ValueFrequencyGraph.createShouldBeVisible$), so a data type
            // must be chosen up front instead of silently inheriting one that
            // may not work at render time.
            required: isFrequencyGraph,
            options: isFrequencyGraph
                ? createDataTypeOptions(compatibleDataTypes)
                : [['', gettext('SELECT_OPTION')], ...createDataTypeOptions(dataTypes)],
            validateOptions: true,
            default: isFrequencyGraph ? compatibleDataTypes[0] : '',
        },
        valueScale: {
            name: 'valueScale',
            label: gettext('VALUE_SCALE'),
            type: 'float',
            required: false,
            default: null,
        },
        valueScaleEquation: {
            name: 'valueScaleEquation',
            label: gettext('VALUE_SCALE_EQUATION'),
            type: 'choice',
            required: false,
            options: [['', gettext('SELECT_OPTION')], ...createValueScaleEquationOptions()],
            validateOptions: true,
            default: '',
        },
        frequencyScale: {
            name: 'frequencyScale',
            label: gettext('FREQUENCY_SCALE'),
            type: 'float',
            required: false,
            default: null,
        },
        ...(includeEnabledAxesField
            ? { enabledAxes: createEnabledAxesField(currentSettings) }
            : {}),
    };
    const settingNames = [
        'graphType',
        'width',
        'height',
        ...getGraphTypeFormSettingNames(graphType),
    ];

    return {
        fields: settingNames.flatMap((settingName) => {
            if (settingName === 'hiddenLines') {
                return [
                    createHiddenLinesModeField(hiddenLinesMode),
                    ...(hiddenLinesMode === HIDDEN_LINES_MODE.CUSTOM
                        ? [
                              createHiddenLinesField(
                                  currentSettings,
                                  dataTypes,
                                  includeEnabledAxesField,
                                  graphType
                              ),
                          ]
                        : []),
                ];
            }

            const field = fieldsByName[settingName];
            return field ? [field] : [];
        }),
    };
}

export function normalizeGraphSettingsForSave(
    currentSettings: ElementSettings,
    nextSettings: ElementSettings,
    touchedSettingNames?: ReadonlySet<string>
): ElementSettings {
    const { graphType } = nextSettings;
    if (!isSupportedGraphType(graphType)) {
        throw new Error(
            `Cannot normalize settings for unsupported graph type: ${String(graphType)}`
        );
    }

    return applyGraphTypeOwnedDataTypeSettings(
        removeSettingsNotUsedByGraphType(
            normalizeHiddenLinesForSave(
                preserveImplicitSetting(
                    currentSettings,
                    preserveImplicitSetting(
                        currentSettings,
                        preserveExplicitNullScaleOverrides(
                            currentSettings,
                            normalizeEnabledAxesForSave(nextSettings)
                        ),
                        touchedSettingNames,
                        'dataType'
                    ),
                    touchedSettingNames,
                    'legendPlacement'
                )
            ),
            graphType
        ),
        graphType
    );
}
